/**
 * Where I am: the planet's rotation under my feet, which way I face, whether I'm moving. Mutated by
 * the input every frame and read by the world and the connection, so it lives outside the store.
 */
import { nearestTile, walkNearest, type Grid } from '@bramvasterd/terrella-rules';

import { quat, quatApply, quatInvert, UP, vec, type Quat, type Vec } from '@core/quat';

export interface Walk {
  from: Quat;
  to: Quat;
  /** performance.now() when the walk started, and how long it takes. */
  start: number;
  duration: number;
}

export interface Local {
  /** The planet's rotation: a planet-local point p is shown at q × p. I stand at the top. */
  q: Quat;
  /** The way I face, in world space (horizontal). */
  heading: Vec;
  /** 1 while moving, decaying to 0. */
  moving: number;
  walk: Walk | null;
  /** The planet's turning speed, in finger pixels per frame (game/spin.ts). */
  vel: { x: number; y: number };
  /** The finger's speed while dragging, and when it was last measured. */
  finger: { x: number; y: number; at: number };
  dragging: boolean;
  /** 0: following me; 1: the whole planet. */
  view: number;
  viewTarget: number;
}

export function createLocal(): Local {
  return {
    q: quat(),
    heading: vec(0, 0, 1),
    moving: 0,
    walk: null,
    vel: { x: 0, y: 0 },
    finger: { x: 0, y: 0, at: 0 },
    dragging: false,
    view: 0,
    viewTarget: 0,
  };
}

const inverse = quat();

/** The planet-local point under my feet (a unit vector). */
export function myPoint(local: Local, out: Vec): Vec {
  quatInvert(inverse, local.q);
  return quatApply(out, inverse, UP);
}

/** The way I face, in planet-local space. */
export function myHeading(local: Local, out: Vec): Vec {
  quatInvert(inverse, local.q);
  return quatApply(out, inverse, local.heading);
}

const point = vec();

/** The tile I stand on now: walked to from `previous` (cheap), or searched for when there is none. */
export function tileUnderMe(local: Local, grid: Grid, previous: number): number {
  myPoint(local, point);
  const x = point[0] ?? 0;
  const y = point[1] ?? 1;
  const z = point[2] ?? 0;
  return previous >= 0 && previous < grid.count
    ? walkNearest(grid, x, y, z, previous)
    : nearestTile(grid, x, y, z);
}
